import { useCallback, useEffect, useState } from 'react'
import { errorMessage, listOnboardingSamples, uploadOnboardingSample } from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { LEVELS, LEVEL_FA } from './constants'
import { POSITIONS, slotGrid } from './onboardingSlots'
import { formatDurationMs, formatDatetime } from './dates'

// ─── Onboarding samples tab (admin-onboarding, V3-p) ──────────────────────────

const inputCls = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'
const btnPrimary = 'rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50'

/**
 * One cell: the sample (play, its text, its length) when the slot is filled,
 * and the upload form — open at once for an empty slot, behind a «جایگزینی»
 * button for a filled one, so a stray click cannot replace a recording.
 * The audio URL is public (the same one learners get), so a plain <audio>.
 */
export function SampleCell({ slot, onUploaded }) {
  const { level, position, sample } = slot
  const [editing, setEditing] = useState(!sample)
  const [textEn, setTextEn] = useState(sample?.textEn ?? '')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [saved, setSaved] = useState(false)

  async function submit(e) {
    e.preventDefault()
    const text = textEn.trim()
    if (!text) { setErr('متن انگلیسی نمونه را بنویسید.'); return }
    if (!file) { setErr('فایل صوتی را انتخاب کنید.'); return }
    setBusy(true); setErr(''); setSaved(false)
    try {
      const stored = await uploadOnboardingSample({ level, position, textEn: text, file })
      setFile(null)
      setEditing(false)
      setSaved(true)
      onUploaded(stored)
    } catch (ex) {
      const code = ex?.response?.data?.error
      if (code === 'audio_rejected') setErr('فایل پذیرفته نشد: خالی، بیش از حد بزرگ، یا با فرمت پشتیبانی‌نشده.')
      else if (code === 'audio_unreadable') setErr('مدت فایل قابل خواندن نبود؛ فایل صوتی دیگری امتحان کنید.')
      else setErr(errorMessage(ex))
    } finally { setBusy(false) }
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>نمونهٔ {faDigits(position + 1)}</span>
        {sample
          ? <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">{formatDurationMs(sample.durationMs)}</span>
          : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">خالی</span>}
      </div>

      {sample && (
        <>
          <audio controls preload="none" src={sample.audioUrl} className="w-full" />
          <p className="text-sm text-slate-800" dir="ltr">{sample.textEn}</p>
          <p className="text-xs text-slate-400">{formatDatetime(sample.updatedAt)}</p>
        </>
      )}

      {!editing && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { setEditing(true); setSaved(false) }} className={btnGhost}>جایگزینی</button>
          {saved && <span className="text-xs text-green-700">ذخیره شد.</span>}
        </div>
      )}

      {editing && (
        <form onSubmit={submit} className="space-y-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">متن انگلیسی</span>
            <input type="text" value={textEn} onChange={e => setTextEn(e.target.value)} dir="ltr"
              placeholder="Could I have a coffee, please?" className={`w-full ${inputCls}`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">فایل صوتی</span>
            <input type="file" accept="audio/*" onChange={e => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-xs text-slate-600 file:me-2 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs" />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={busy} className={btnPrimary}>
              {busy ? 'در حال بارگذاری…' : sample ? 'جایگزین کن' : 'بارگذاری'}
            </button>
            {sample && (
              <button type="button" disabled={busy} onClick={() => { setEditing(false); setFile(null); setErr(''); setTextEn(sample.textEn) }} className={btnGhost}>انصراف</button>
            )}
          </div>
          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        </form>
      )}
    </div>
  )
}

/** The 3×3 grid, given the nine slots: one row per level, one cell per position. */
export function OnboardingGrid({ items, onUploaded }) {
  const grid = slotGrid(items)
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        سه نمونه برای هر سطح؛ زبان‌آموز پیش از ورود آن‌ها را می‌شنود تا سطح خود را انتخاب کند. بارگذاری در یک خانه، نمونهٔ قبلی همان خانه را جایگزین می‌کند.
      </p>
      {LEVELS.map(level => (
        <section key={level}>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">{LEVEL_FA[level]}</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {POSITIONS.map(position => (
              <SampleCell key={`${level}-${position}`} slot={grid[level][position]} onUploaded={onUploaded} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

/** The onboarding samples tab (V3-p): loads `admin-onboarding_list`, renders the 3×3 grid, replaces a slot as it is uploaded. */
export function OnboardingTab() {
  const [items, setItems] = useState(null)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try { setItems(await listOnboardingSamples()) }
    catch (ex) { setErr(errorMessage(ex)) }
  }, [])

  useEffect(() => { load() }, [load])

  function uploaded(sample) {
    setItems(list => list.map(s => s.level === sample.level && s.position === sample.position ? { ...s, sample } : s))
  }

  if (err) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>
        <button type="button" onClick={load} className={btnGhost}>تلاش دوباره</button>
      </div>
    )
  }
  if (items === null) return <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>
  return <OnboardingGrid items={items} onUploaded={uploaded} />
}
